// frontend/src/utils/servicePricing.js
// Mirror of backend utils/servicePricing.js for DISPLAY ONLY.
// The server is authoritative for money - never trust these values for charge.
//
// Resolved line shape:
//   { pricingType, quantity, unitPrice, unitMinutes, totalPrice, totalMinutes,
//     label, unitLabel, maxQuantity, supported, requiresSelection,
//     selectionKind, selectionOptions, selectionDriven }

export const SUPPORTED_PRICING_TYPES = ["fixed", "per_unit", "hourly", "range"];

export function supportsPricingType(pricingType) {
  return SUPPORTED_PRICING_TYPES.includes(
    String(pricingType || "").toLowerCase(),
  );
}

function normalizePricingType(pricingType) {
  return String(pricingType || "fixed").toLowerCase();
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function roundMinutes(value) {
  return Math.round(Number(value) || 0);
}

function getServiceDuration(service) {
  return Number(service?.durationMinutes) || Number(service?.duration) || 0;
}

// --- per unit ---------------------------------------------------------------

export function isPerUnitService(service) {
  return normalizePricingType(service?.pricingType) === "per_unit";
}

export function getPerUnitPrice(service) {
  const n = Number(service?.pricePerUnit);
  if (Number.isFinite(n) && n >= 0) return n;
  const legacy = Number(service?.price);
  return Number.isFinite(legacy) ? legacy : 0;
}

export function getPerUnitMinutes(service) {
  const n = Number(service?.perUnitMinutes);
  if (Number.isFinite(n) && n > 0) return n;
  const legacy = Number(service?.durationMinutes);
  return Number.isFinite(legacy) && legacy > 0 ? legacy : 0;
}

export function getMaxUnits(service) {
  const n = Number(service?.maxUnitsPerBooking);
  if (Number.isFinite(n) && n >= 1 && n <= 100) return Math.floor(n);
  return 10;
}

export function clampQuantity(service, quantity) {
  const max = getMaxUnits(service);
  const raw = Math.floor(Number(quantity));
  if (!Number.isFinite(raw) || raw < 1) return 1;
  return Math.min(raw, max);
}

// --- hourly -----------------------------------------------------------------

export function isHourlyService(service) {
  return normalizePricingType(service?.pricingType) === "hourly";
}

/** Hourly rate, or 0 when none is set (legacy row keeps its flat price). */
export function getHourlyRate(service) {
  const n = Number(service?.hourlyRate);
  if (Number.isFinite(n) && n > 0) return n;
  return 0;
}

export function getMaxHours(service) {
  const n = Number(service?.maxHoursPerBooking);
  if (Number.isFinite(n) && n >= 1 && n <= 24) return Math.floor(n);
  return 8;
}

export function clampHours(service, hours) {
  const max = getMaxHours(service);
  const raw = Math.floor(Number(hours));
  if (!Number.isFinite(raw) || raw < 1) return 1;
  return Math.min(raw, max);
}

// --- tiered range -----------------------------------------------------------

export function isRangeService(service) {
  return normalizePricingType(service?.pricingType) === "range";
}

export function getPriceTiers(service) {
  const raw = Array.isArray(service?.priceTiers) ? service.priceTiers : [];
  return raw
    .map((tier, index) => {
      const price = Number(tier?.price);
      if (!Number.isFinite(price) || price <= 0) return null;
      const duration = Number(tier?.durationMinutes);
      return {
        id: String(tier?._id || tier?.id || index),
        index,
        label: String(tier?.label || "").trim() || `Option ${index + 1}`,
        price,
        durationMinutes:
          Number.isFinite(duration) && duration > 0 ? duration : null,
      };
    })
    .filter(Boolean);
}

export function getTierPriceBounds(service) {
  const tiers = getPriceTiers(service);
  if (tiers.length === 0) return { min: 0, max: 0 };
  const prices = tiers.map((tier) => tier.price);
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export function resolveTier(service, selection = {}) {
  const tiers = getPriceTiers(service);
  if (tiers.length === 0) return null;

  const requestedId = selection?.tierId ?? selection?.id ?? null;
  if (requestedId !== null && requestedId !== undefined && requestedId !== "") {
    const byId = tiers.find((tier) => tier.id === String(requestedId));
    if (byId) return byId;

    // Mirrors the server: a payload carrying the tier's POSITION where an id
    // belongs still counts as a choice. See utils/servicePricing.js.
    const asPosition = Number(requestedId);
    if (Number.isInteger(asPosition) && asPosition >= 0) {
      const byPosition = tiers.find((tier) => tier.index === asPosition);
      if (byPosition) return byPosition;
    }
  }

  // Only honour an index the caller actually supplied. `Number(null)` is 0, so a
  // bare `tierIndex: null` used to silently select the FIRST - cheapest - tier
  // instead of forcing the customer to choose. Absent / null / empty must mean
  // "no choice made yet".
  const rawIndex = selection?.tierIndex;
  if (rawIndex !== null && rawIndex !== undefined && rawIndex !== "") {
    const requestedIndex = Number(rawIndex);
    if (Number.isFinite(requestedIndex)) {
      const byIndex = tiers.find((tier) => tier.index === requestedIndex);
      if (byIndex) return byIndex;
    }
  }

  return null;
}

// --- shape checks -----------------------------------------------------------

export function isSelectionDrivenService(service) {
  if (!service) return false;
  const type = normalizePricingType(service.pricingType);

  if (type === "per_unit") return true;
  if (type === "hourly") return getHourlyRate(service) > 0;
  if (type === "range") return getPriceTiers(service).length >= 1;

  return false;
}

export function getSelectionKind(service) {
  if (!service) return null;
  const type = normalizePricingType(service.pricingType);

  if (type === "per_unit") return "quantity";
  if (type === "hourly") return getHourlyRate(service) > 0 ? "quantity" : null;
  if (type === "range") return getPriceTiers(service).length >= 1 ? "tier" : null;

  return null;
}

function buildLabel(service, quantity, suffix, unit) {
  const name = service?.name || service?.serviceName || "Service";
  // Keep this identical to the server engine so displayed and stored labels match.
  if (Number(quantity) > 1) {
    return unit ? `${name} × ${quantity} ${unit}s` : `${name} × ${quantity}`;
  }
  if (unit) return `${name} (1 ${unit})`;
  if (suffix) return `${name} (${suffix})`;
  return name;
}

/**
 * Resolve a service into a priced line for display.
 * @param {Object} service
 * @param {Object} selection - e.g. { quantity } / { hours } / { tierId }
 * @param {Object} [opts] - { allowQuantity }
 */
export function resolveServicePricing(service, selection = {}, opts = {}) {
  if (!service) return null;

  const pricingType = normalizePricingType(service.pricingType);
  const serviceDuration = getServiceDuration(service);
  const serviceId = service._id || service.id || service.serviceId || null;

  if (pricingType === "per_unit") {
    const allowQuantity = opts.allowQuantity !== false;
    const quantity = allowQuantity
      ? clampQuantity(service, selection.quantity)
      : 1;
    const unitPrice = getPerUnitPrice(service);
    const unitMinutes = getPerUnitMinutes(service);
    return {
      serviceId,
      pricingType,
      quantity,
      unitPrice,
      unitMinutes,
      totalPrice: roundMoney(unitPrice * quantity),
      totalMinutes: roundMinutes(unitMinutes * quantity),
      label: buildLabel(service, quantity),
      unitLabel: String(service.unitLabel || service.unit || "unit").trim(),
      maxQuantity: getMaxUnits(service),
      supported: true,
      requiresSelection: false,
      selectionKind: "quantity",
      selectionOptions: [],
      selectionDriven: true,
    };
  }

  if (pricingType === "hourly") {
    const rate = getHourlyRate(service);
    if (rate > 0) {
      const allowQuantity = opts.allowQuantity !== false;
      const hours = allowQuantity ? clampHours(service, selection.hours) : 1;
      return {
        serviceId,
        pricingType,
        quantity: hours,
        unitPrice: rate,
        unitMinutes: 60,
        totalPrice: roundMoney(rate * hours),
        totalMinutes: roundMinutes(60 * hours),
        label: buildLabel(service, hours, null, "hr"),
        unitLabel: "hour",
        maxQuantity: getMaxHours(service),
        supported: true,
        requiresSelection: false,
        selectionKind: "quantity",
        selectionOptions: [],
        selectionDriven: true,
      };
    }
  }

  if (pricingType === "range") {
    const tiers = getPriceTiers(service);
    if (tiers.length >= 1) {
      const selectionOptions = tiers.map((tier) => ({
        id: tier.id,
        label: tier.label,
        price: tier.price,
        durationMinutes: tier.durationMinutes,
      }));
      const tier = resolveTier(service, selection);

      if (!tier) {
        return {
          serviceId,
          pricingType,
          quantity: 1,
          unitPrice: 0,
          unitMinutes: 0,
          totalPrice: 0,
          totalMinutes: 0,
          label: service.name || service.serviceName || "Service",
          unitLabel: "option",
          maxQuantity: 1,
          supported: true,
          requiresSelection: true,
          selectionKind: "tier",
          selectionOptions,
          selectionDriven: true,
        };
      }

      const unitMinutes = tier.durationMinutes || serviceDuration;
      return {
        serviceId,
        pricingType,
        quantity: 1,
        unitPrice: tier.price,
        unitMinutes,
        totalPrice: roundMoney(tier.price),
        totalMinutes: roundMinutes(unitMinutes),
        label: buildLabel(service, 1, tier.label),
        unitLabel: "option",
        maxQuantity: 1,
        supported: true,
        requiresSelection: false,
        selectionKind: "tier",
        selectionOptions,
        selectionDriven: true,
      };
    }
  }

  const unitPrice = Number(service.price) || 0;
  return {
    serviceId,
    pricingType,
    quantity: 1,
    unitPrice,
    unitMinutes: serviceDuration,
    totalPrice: unitPrice,
    totalMinutes: serviceDuration,
    label: buildLabel(service, 1),
    unitLabel: null,
    maxQuantity: 1,
    supported: supportsPricingType(pricingType),
    requiresSelection: false,
    selectionKind: null,
    selectionOptions: [],
    selectionDriven: false,
  };
}

export function resolveServiceLines(items = []) {
  const lines = items.map((item) =>
    resolveServicePricing(item?.service, item?.selection || {}, item?.opts || {}),
  );
  const totalPrice = lines.reduce(
    (sum, l) => sum + (Number(l?.totalPrice) || 0),
    0,
  );
  const totalMinutes = lines.reduce(
    (sum, l) => sum + (Number(l?.totalMinutes) || 0),
    0,
  );
  return { lines, totalPrice, totalMinutes };
}
